import { mutate } from 'swr';
import { deleteAPI } from '~/lib/api';
import { useUser } from './useUser';
import type { Database, InterestLevel, NestedHome } from '@habity/types';
import { useFilters } from './useFilters';
import { useTracking } from './useTracking';
import { useAgency } from './useAgency';
import { EVENTS } from '@habity/constants';
import { Icon } from '@chakra-ui/react';
import { useToast } from '~/lib/toasts';
import { ICONS } from '~/constants';
import { useCallback } from 'react';
import { useUserAction } from './useUserAction';

export const useInterest = () => {
  const { user } = useUser();
  const { getSearchRoute } = useFilters();
  const { track } = useTracking();
  const { agency } = useAgency();
  const { showToast } = useToast();

  // Use centralized action hook
  const { executeAction, isLoading: isAddingInterest } = useUserAction();

  /**
   * Add interest using unified flow via useUserAction:
   * - Logged-in users with complete profiles: action performed immediately, confirmation screen shown
   * - Logged-out users OR incomplete profiles: login modal shown at appropriate step
   */
  const addToInterest = useCallback(
    async (
      home: NestedHome,
      interest_level: Database['public']['Enums']['interest_level']
    ) => {
      const result = await executeAction({
        home,
        action: interest_level,
        redirectTo: getSearchRoute(home.id.toString()),
      });

      if (!result.success && result.error) {
        showToast(
          {
            title: 'Något gick fel',
            description: result.error,
          },
          { status: 'error' }
        );
      }
    },
    [executeAction, getSearchRoute, showToast]
  );

  const removeFromInterest = async (
    home: NestedHome,
    interest_level: InterestLevel
  ) => {
    if (!user) return;

    // Find the interest ID from user.interests
    const interest = user.interests?.find(
      (i) => i.home_id === home.id && i.interest_level === interest_level
    );

    if (!interest?.id) {
      console.error('Interest not found for removal');
      return;
    }

    // Delete via backend (handles both Supabase delete and VITEC sync)
    await deleteAPI(`/providers/supabase/interests/${interest.id}`);

    // Trigger SWR revalidation after backend delete completes
    mutate(['users', user.id]);

    // Track interest remove event
    track(EVENTS.REMOVED_INTEREST, {
      homeId: home.id,
      userId: user.id,
      agencyId: agency?.id,
      homeAddress: home.address?.street,
      homeCity: home.address?.city,
      homeZip: home.address?.zip,
      homeStatus: home.status,
      interestLevel: interest_level,
    });

    // Use consistent toast styling for removal messages too with deduplication
    if (interest_level === 'showed_interest') {
      showToast(
        {
          title: 'Intresse avanmält',
          description: `Du har avanmält intresse för ${home?.address?.street}.`,
        },
        {
          icon: <Icon as={ICONS.INTEREST} boxSize={6} />,
        }
      );
    } else if (interest_level === 'followed_bidding') {
      showToast(
        {
          title: 'Budgivningsföljning avslutad',
          description: `Du följer inte längre budgivningen av ${home?.address?.street}.`,
        },
        {
          icon: <Icon as={ICONS.BID} boxSize={6} />,
        }
      );
    }
  };

  return { addToInterest, removeFromInterest, isAddingInterest };
};
